import React, { useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Loader2, CheckCircle, AlertCircle, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const API = `${window.location.origin}/api`;

// Minimal CSV parser (handles quoted fields, commas, CRLF). Good enough for our supplier exports.
const parseCsv = (text) => {
  const rows = [];
  let cur = [];
  let field = "";
  let inQuotes = false;
  const push = () => { cur.push(field); field = ""; };
  const pushRow = () => { rows.push(cur); cur = []; };
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 1; }
        else { inQuotes = false; }
      } else { field += c; }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      push();
    } else if (c === "\r") {
      // skip
    } else if (c === "\n") {
      push(); pushRow();
    } else {
      field += c;
    }
  }
  if (field.length || cur.length) { push(); pushRow(); }
  return rows.filter((r) => r.some((cell) => (cell || "").trim().length > 0));
};

// Candidate header names we can auto-detect
const GUESS_MAP = {
  name: ["item", "name", "material", "description", "product"],
  quantity: ["qty", "quantity", "amount", "count"],
  unit: ["unit", "uom", "units"],
  unit_cost: ["cost", "price", "unit cost", "unit price", "rate", "unit_cost", "unit_price"],
  supplier: ["supplier", "vendor", "merchant", "shop"],
};

const guessColumn = (headers, candidates) => {
  const lower = headers.map((h) => (h || "").trim().toLowerCase());
  for (let i = 0; i < lower.length; i += 1) {
    if (candidates.includes(lower[i])) return i;
  }
  return -1;
};


export const CsvImportDialog = ({ open, onClose, onImported, authHeaders, enquiries }) => {
  const fileInput = useRef(null);
  const [rawRows, setRawRows] = useState([]); // string[][]
  const [headerRow, setHeaderRow] = useState(0);
  const [mapping, setMapping] = useState({ name: -1, quantity: -1, unit: -1, unit_cost: -1, supplier: -1 });
  const [jobId, setJobId] = useState("");
  const [defaultStatus, setDefaultStatus] = useState("to_order");
  const [history, setHistory] = useState({}); // rowIndex -> {unit_cost, supplier, last_used_at}
  const [fetchingHistory, setFetchingHistory] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const reset = () => {
    setRawRows([]); setHeaderRow(0);
    setMapping({ name: -1, quantity: -1, unit: -1, unit_cost: -1, supplier: -1 });
    setHistory({}); setImportResult(null);
  };

  const handleFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    const rows = parseCsv(text);
    if (!rows.length) { toast.error("CSV is empty"); return; }
    setRawRows(rows);
    // Auto-guess mapping from first row
    const headers = rows[0];
    setMapping({
      name: guessColumn(headers, GUESS_MAP.name),
      quantity: guessColumn(headers, GUESS_MAP.quantity),
      unit: guessColumn(headers, GUESS_MAP.unit),
      unit_cost: guessColumn(headers, GUESS_MAP.unit_cost),
      supplier: guessColumn(headers, GUESS_MAP.supplier),
    });
    setHeaderRow(0);
  };

  const dataRows = rawRows.slice(headerRow + 1);
  const headers = rawRows[headerRow] || [];

  const buildPreview = useCallback(() => {
    return dataRows.map((r, idx) => {
      const get = (i) => (i >= 0 ? (r[i] || "").trim() : "");
      const name = get(mapping.name);
      const quantity = parseFloat(get(mapping.quantity)) || 1;
      const unit = get(mapping.unit) || "each";
      const unit_cost = parseFloat((get(mapping.unit_cost) || "").replace(/[£$,]/g, "")) || 0;
      const supplier = get(mapping.supplier);
      return { idx, name, quantity, unit, unit_cost, supplier };
    }).filter((r) => r.name);
  }, [dataRows, mapping]);

  const preview = buildPreview();

  // Look up historical prices for the parsed rows — used to suggest recent prices
  const lookupHistory = async () => {
    if (!preview.length) return;
    setFetchingHistory(true);
    const next = {};
    try {
      const lookups = await Promise.all(
        preview.map((row) =>
          axios.get(`${API}/admin/materials/history-match?name=${encodeURIComponent(row.name)}`, authHeaders)
            .then((res) => res.data?.match || null)
            .catch(() => null)
        )
      );
      preview.forEach((row, i) => { if (lookups[i]) next[row.idx] = lookups[i]; });
      setHistory(next);
      const suggested = Object.keys(next).length;
      toast.success(suggested > 0 ? `Found ${suggested} historical matches` : "No historical matches for these items yet");
    } finally {
      setFetchingHistory(false);
    }
  };

  const applyHistorical = (rowIdx) => {
    const h = history[rowIdx];
    if (!h) return;
    const previewIdx = preview.findIndex((p) => p.idx === rowIdx);
    if (previewIdx < 0) return;
    const rawRowIdx = headerRow + 1 + previewIdx;
    const newRawRows = [...rawRows];
    const current = [...(newRawRows[rawRowIdx] || [])];
    if (mapping.unit_cost >= 0) current[mapping.unit_cost] = String(h.unit_cost);
    if (mapping.supplier >= 0) current[mapping.supplier] = h.supplier || "";
    newRawRows[rawRowIdx] = current;
    setRawRows(newRawRows);
  };

  const handleImport = async () => {
    if (!jobId) { toast.error("Select a job to attach these materials to"); return; }
    if (mapping.name < 0) { toast.error("Map the 'Item / Name' column"); return; }
    if (!preview.length) { toast.error("Nothing to import"); return; }
    setImporting(true);
    try {
      const payload = {
        job_id: jobId,
        rows: preview.map((p) => ({
          name: p.name,
          quantity: p.quantity,
          unit: p.unit,
          unit_cost: p.unit_cost,
          supplier: p.supplier,
          status: defaultStatus,
          notes: "",
        })),
      };
      const res = await axios.post(`${API}/admin/materials/import-csv`, payload, authHeaders);
      setImportResult(res.data);
      if (res.data.errors?.length) {
        toast.warning(`Imported ${res.data.created} rows — ${res.data.errors.length} errors`);
      } else {
        toast.success(`Imported ${res.data.created} materials`);
      }
      // Slight delay so user sees result banner before dialog closes
      setTimeout(() => {
        onImported?.();
        reset();
      }, 1200);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && (onClose?.(), reset())}>
      <DialogContent className="max-w-3xl" data-testid="csv-import-dialog">
        <DialogHeader>
          <DialogTitle>Import materials from CSV</DialogTitle>
          <DialogDescription>
            Upload a supplier export or custom CSV. We'll auto-guess the columns, then suggest recent prices from your history before importing.
          </DialogDescription>
        </DialogHeader>

        {!rawRows.length ? (
          <div
            onClick={() => fileInput.current?.click()}
            className="border-2 border-dashed rounded-lg px-4 py-10 text-center cursor-pointer hover:bg-muted/30"
            data-testid="csv-import-dropzone"
          >
            <Upload className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
            <p className="font-medium">Upload a CSV file</p>
            <p className="text-xs text-muted-foreground mt-1">
              Expected columns: Item, Qty, Unit, Cost, Supplier (any order — we auto-detect)
            </p>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => { const f = e.target.files?.[0]; handleFile(f); e.target.value = ""; }}
              data-testid="csv-import-file-input"
            />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Job + defaults row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="sm:col-span-2">
                <Label className="text-xs">Attach to job *</Label>
                <Select value={jobId} onValueChange={setJobId}>
                  <SelectTrigger data-testid="csv-import-job-select"><SelectValue placeholder="Pick a job / enquiry" /></SelectTrigger>
                  <SelectContent>
                    {(enquiries || []).slice(0, 100).map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name} · {e.service || "job"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Default status</Label>
                <Select value={defaultStatus} onValueChange={setDefaultStatus}>
                  <SelectTrigger data-testid="csv-import-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="to_order">To Order</SelectItem>
                    <SelectItem value="ordered">Ordered</SelectItem>
                    <SelectItem value="received">Received</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Column mapping */}
            <div>
              <Label className="text-xs">Column mapping</Label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-1">
                {Object.keys(mapping).map((key) => (
                  <div key={key}>
                    <p className="text-xs text-muted-foreground capitalize mb-0.5">{key.replace("_", " ")}{key === "name" ? " *" : ""}</p>
                    <Select value={String(mapping[key])} onValueChange={(v) => setMapping((p) => ({ ...p, [key]: Number(v) }))}>
                      <SelectTrigger className="h-8" data-testid={`csv-import-map-${key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="-1">— skip —</SelectItem>
                        {headers.map((h, i) => (
                          <SelectItem key={i} value={String(i)}>{h || `Column ${i + 1}`}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            {/* Historical match button + preview */}
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {preview.length} parsed row{preview.length === 1 ? "" : "s"} — {Object.keys(history).length} with historical price suggestions
              </p>
              <Button size="sm" variant="outline" onClick={lookupHistory} disabled={fetchingHistory} data-testid="csv-import-history-btn">
                {fetchingHistory ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                Suggest recent prices
              </Button>
            </div>

            <div className="border rounded max-h-[280px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted/50 border-b">
                  <tr>
                    <th className="text-left p-2">Item</th>
                    <th className="text-left p-2 w-16">Qty</th>
                    <th className="text-left p-2 w-16">Unit</th>
                    <th className="text-left p-2 w-20">Cost</th>
                    <th className="text-left p-2">Supplier</th>
                    <th className="text-left p-2 w-24">Match</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row) => {
                    const h = history[row.idx];
                    return (
                      <tr key={row.idx} className="border-b last:border-0" data-testid={`csv-import-row-${row.idx}`}>
                        <td className="p-2">{row.name}</td>
                        <td className="p-2">{row.quantity}</td>
                        <td className="p-2">{row.unit}</td>
                        <td className="p-2">£{row.unit_cost.toFixed(2)}</td>
                        <td className="p-2">{row.supplier}</td>
                        <td className="p-2">
                          {h ? (
                            <button
                              type="button"
                              onClick={() => applyHistorical(row.idx)}
                              className="text-xs text-primary underline hover:no-underline"
                              title={`Last used: ${h.last_used_at ? new Date(h.last_used_at).toLocaleDateString() : "-"}`}
                              data-testid={`csv-import-apply-history-${row.idx}`}
                            >
                              Use £{Number(h.unit_cost).toFixed(2)}
                            </button>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {importResult && (
              <div className="rounded-md border p-3 text-sm flex items-start gap-2 bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-green-900">Imported {importResult.created} materials</p>
                  {importResult.errors?.length > 0 && (
                    <p className="text-xs text-amber-700 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{importResult.errors.length} rows skipped</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {rawRows.length > 0 && (
            <Button variant="outline" onClick={reset} data-testid="csv-import-reset-btn">Change file</Button>
          )}
          <Button variant="outline" onClick={() => { onClose?.(); reset(); }}>Cancel</Button>
          {rawRows.length > 0 && (
            <Button onClick={handleImport} disabled={importing || !jobId || !preview.length} data-testid="csv-import-submit-btn">
              {importing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
              Import {preview.length} row{preview.length === 1 ? "" : "s"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CsvImportDialog;
